/* PRD-037 — Componentes de gestion compartidos (V4). */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PartnerCommerce } from '@services/partnerService';
import { TextInputV4 } from '@components/v4/Input/Input';
import { Badge, C } from './ui';
import { fmtDate } from './useCommerces';

export const Stat = ({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) => (
  <div
    style={{
      flex: 1,
      minWidth: 120,
      background: C.white,
      border: `1px solid ${C.cardBorder}`,
      borderRadius: 12,
      padding: '16px 18px',
    }}
  >
    <div style={{ fontSize: 26, fontWeight: 700, color: tone || C.primary, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{label}</div>
  </div>
);

// Acciones rapidas por fila (solo drafts). El claim-link se genera y se muestra inline sin entrar
// al detalle. Cada callback es opcional: si no se pasa `quick`, la fila es solo navegable al detalle.
export interface CommerceQuickActions {
  onView: () => void;
  onEditContact: () => void;
  onConfigure: () => void;
  onLoadMenu: () => void;
  onClaimLink: () => Promise<string>;
}

const RowBtn = ({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      fontFamily: 'Poppins, sans-serif',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.02em',
      color: C.secondary,
      background: C.white,
      border: `1px solid ${C.secondary}`,
      borderRadius: 6,
      padding: '5px 10px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </button>
);

export const CommerceItem = ({ c, quick }: { c: PartnerCommerce; quick?: CommerceQuickActions }) => {
  const navigate = useNavigate();
  const claimed = c.estado === 'reclamado';
  const [link, setLink] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);
  const [linkError, setLinkError] = useState(false);

  const onClaimLink = async () => {
    if (!quick) return;
    setLinkError(false);
    setGenLoading(true);
    try {
      setLink(await quick.onClaimLink());
    } catch {
      setLinkError(true);
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div style={{ borderTop: `1px solid ${C.cardBorder}`, padding: '14px 0' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => c.id_comercio && navigate(`/partner/comercios/${c.id_comercio}`)}
          style={{
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: C.primary }}>
              {c.negocio_nombre || 'Comercio sin nombre'}
            </span>
            <Badge tone={claimed ? 'claimed' : 'draft'}>{claimed ? 'Activado' : 'Por activar'}</Badge>
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            {c.owner_nombre || 'Sin dueno'}
            {c.owner_telefono ? ` · ${c.owner_telefono}` : ''}
            {c.owner_correo ? ` · ${c.owner_correo}` : ''}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            {c.tipo_plan || 'FREEMIUM'}
            {c.created_at ? ` · creado ${fmtDate(c.created_at)}` : ''}
          </div>
        </button>

        {quick ? (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <RowBtn onClick={quick.onView}>Ver</RowBtn>
            {!claimed && (
              <>
                <RowBtn onClick={quick.onConfigure}>Preparar</RowBtn>
                <RowBtn onClick={quick.onLoadMenu}>Cargar menú</RowBtn>
                <RowBtn onClick={quick.onEditContact}>Editar contacto</RowBtn>
                <RowBtn onClick={onClaimLink} disabled={genLoading}>
                  {genLoading ? 'Generando...' : 'Invitar al dueño'}
                </RowBtn>
              </>
            )}
          </div>
        ) : null}
      </div>

      {linkError && (
        <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>No se pudo generar el enlace de activación.</div>
      )}
      {link && (
        <div style={{ marginTop: 8 }}>
          <TextInputV4
            value={link}
            onChange={() => {}}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Enlace de activación"
          />
        </div>
      )}
    </div>
  );
};
